// ✅ src/pages/OrderManagement/RiskAndIssue.jsx
// FULL FILE — header-aware version with localStorage headers
//
// Features kept:
// - Load grid by contractId
// - Click Risk Title => edit mode
// - Add New => clear form
// - Submit => insert/update
// - Delete selected
// - Print => opens print page in new tab
// - Reads finYear / finYearId / branchId / branchType / userId from localStorage
// - Sends header context in every API call

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaSave,
  FaUndo,
  FaPlus,
  FaPrint,
  FaTrash,
  FaSpinner,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";

const API = "https://nissi-india.com/api/riskIssues";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const RiskIssuesComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState("Risk/Issues");

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");
  const usernameLS =
    getLS("username") || getLS("UserName") || getLS("userName") || userIdLS;

  // ✅ numeric userId if needed
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  // ✅ contractId from URL or sessionStorage
  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";
    const cid = Number(fromUrl || fromSession || 0);
    return Number.isFinite(cid) ? cid : 0;
  }, [location.search]);

  useEffect(() => {
    if (contractId > 0) {
      sessionStorage.setItem("Selcontractid", String(contractId));
    }
  }, [contractId]);

  const withContractId = (path) => {
    if (!contractId) return path;
    const url = new URL(path, window.location.origin);
    url.searchParams.set("contractId", contractId);
    return url.pathname + url.search;
  };

  const sidebarItems = useMemo(
    () => [
      { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
      { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ") },
      { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
      { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
      { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
      { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
      { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
      { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
      { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
      { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
      { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
    ],
    [contractId]
  );

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem.name);
  };

  // ✅ form
  const [formData, setFormData] = useState({
    riskTitle: "",
    riskIssue: "",
    responsibility: "",
    resolution: "",
  });

  // ✅ edit flag
  const [editingRiskId, setEditingRiskId] = useState(0);
  const mode = editingRiskId > 0 ? "U" : "N";

  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const clearForm = () => {
    setFormData({
      riskTitle: "",
      riskIssue: "",
      responsibility: "",
      resolution: "",
    });
    setEditingRiskId(0);
  };

  const loadList = async () => {
    if (!contractId) {
      setRows([]);
      setError("contractId missing in URL");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`${API}/list`, {
        params: { contractId },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load Risk/Issues list");
        setRows([]);
        return;
      }

      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(list);
      setSelectedIds(new Set());
    } catch (e) {
      console.error("riskIssues list error:", e);
      setError("Failed to load Risk/Issues list");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const moduleSelect =
      sessionStorage.getItem("ModuleSelect") ||
      sessionStorage.getItem("moduleSelect") ||
      "ERP";

    if (String(moduleSelect).toUpperCase() !== "ERP") {
      window.location.href = "/ModuleHomePage";
      return;
    }

    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, commonHeaders]);

  const handleView = async (riskId) => {
    const id = Number(riskId || 0);
    if (!Number.isFinite(id) || id <= 0) return;

    try {
      const res = await axios.get(`${API}/view/${id}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        alert(res.data?.message || "Failed to load record");
        return;
      }

      const r = res.data?.data || {};
      setFormData({
        riskTitle: String(r.fld_Risktitle ?? ""),
        riskIssue: String(r.fld_Riskissue ?? ""),
        responsibility: String(r.fld_Responsibility ?? ""),
        resolution: String(r.fld_Resolution ?? ""),
      });
      setEditingRiskId(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error("riskIssues view error:", e);
      alert("Failed to load record");
    }
  };

  const validate = () => {
    if (!String(formData.riskTitle || "").trim()) {
      alert("Please enter Title..");
      return false;
    }
    if (!String(formData.responsibility || "").trim()) {
      alert("Please enter Responsibility..");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (saving) return;

    if (!contractId) {
      alert("contractId missing in URL");
      return;
    }

    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        contractId,
        mode,
        riskId: editingRiskId,
        riskTitle: String(formData.riskTitle ?? "").trim(),
        riskIssue: String(formData.riskIssue ?? "").trim(),
        responsibility: String(formData.responsibility ?? "").trim(),
        resolution: String(formData.resolution ?? "").trim(),
        userId,
        username: usernameLS,
        branchId: branchIdLS,
        branchType: branchTypeLS,
      };

      const res = await axios.post(`${API}/save`, payload, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        alert(res.data?.message || "Save failed");
        return;
      }

      alert(
        res.data?.message ||
          (mode === "U" ? "Updated Successfully" : "Successfully Inserted")
      );

      clearForm();
      await loadList();
    } catch (e) {
      console.error("riskIssues save error:", e);
      alert("Server error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = () => {
    clearForm();
  };

  const toggleSelect = (riskId) => {
    const id = Number(riskId);
    if (!Number.isFinite(id) || id <= 0) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!rows.length) return;

    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(
        rows
          .map((r) => Number(r.fld_Riskid))
          .filter((x) => Number.isFinite(x) && x > 0)
      );
    });
  };

  const handleDeleteSelected = async () => {
    if (deleting) return;

    const ids = Array.from(selectedIds);
    if (!ids.length) {
      alert("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    setDeleting(true);
    try {
      const res = await axios.post(
        `${API}/delete`,
        {
          riskIds: ids,
          contractId,
          userId,
          branchId: branchIdLS,
          branchType: branchTypeLS,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        alert(res.data?.message || "Please retry");
        return;
      }

      alert(res.data?.message || "Removed Successfully");
      clearForm();
      await loadList();
    } catch (e) {
      console.error("riskIssues delete error:", e);
      alert("Server error");
    } finally {
      setDeleting(false);
    }
  };

  const handlePrint = async () => {
    if (printing) return;

    if (!contractId) {
      alert("contractId missing");
      return;
    }

    const payloadRows = rows.map((r, idx) => ({
      sno: idx + 1,
      fld_Riskid: r.fld_Riskid,
      fld_Risktitle: r.fld_Risktitle,
      fld_Riskissue: r.fld_Riskissue,
      fld_Responsibility: r.fld_Responsibility,
      fld_Resolution: r.fld_Resolution,
      Createdon: r.Createdon,
    }));

    setPrinting(true);
    try {
      const res = await axios.post(
        `${API}/printData`,
        {
          contractId,
          rows: payloadRows,
          userId,
          branchId: branchIdLS,
          branchType: branchTypeLS,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        alert(res.data?.message || "Print failed");
        return;
      }

      sessionStorage.setItem("RiskIssuesPrint_payload", JSON.stringify(res.data));
      window.open(
        `/OrderManagement/RiskIssuesPrint?contractId=${contractId}`,
        "_blank",
        "width=1200,height=900,scrollbars=yes"
      );
    } catch (e) {
      console.error("riskIssues print error:", e);
      alert("Print failed");
    } finally {
      setPrinting(false);
    }
  };

  const totalCount = rows.length;
  const selectedCount = selectedIds.size;

  return (
    <div
      className={`min-h-screen transition-all duration-300 ${
        isDark ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      {/* Top Bar */}
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <span
                  className={`font-semibold ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Project Management System
                </span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                    : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? "w-64" : "w-16"
          } transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                onClick={() => handleMenuItemClick(item)}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  location.pathname === new URL(item.path, window.location.origin).pathname
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && (
                  <span className="text-sm font-medium">{item.name}</span>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div
            className={`transition-all duration-300 min-h-screen ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            <div className="p-6">
              {/* Header */}
              <div
                className={`backdrop-blur-md border rounded-2xl mb-6 ${
                  isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
                }`}
              >
                <div className="px-6 py-4">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                    <div>
                      <h1
                        className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <FaExclamationTriangle className="text-orange-500" />
                        RISK AND ISSUES
                      </h1>

                      <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Risk and Issues Management System
                      </p>

                      <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        ContractId: {contractId || "-"} | Mode:{" "}
                        {mode === "U" ? "Update" : "Save"} | BranchId:{" "}
                        {branchIdLS || "-"} | BranchType: {branchTypeLS || "-"} | Active Menu:{" "}
                        {activeMenuItem}
                      </p>

                      {error ? (
                        <p className={`text-xs mt-1 ${isDark ? "text-red-300" : "text-red-600"}`}>
                          {error}
                        </p>
                      ) : null}

                      {loading ? (
                        <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                          Loading...
                        </p>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={handleSubmit}
                        disabled={saving || loading}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                        {saving ? "Saving..." : "Submit"}
                      </button>

                      <button
                        onClick={handleAddNew}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        <FaPlus />
                        Add New
                      </button>

                      <button
                        onClick={handlePrint}
                        disabled={printing || loading}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        {printing ? <FaSpinner className="animate-spin" /> : <FaPrint />}
                        {printing ? "Printing..." : "Print"}
                      </button>

                      <button
                        onClick={handleDeleteSelected}
                        disabled={deleting || loading}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        {deleting ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                        {deleting ? "Deleting..." : "Delete Selected"}
                      </button>

                      <button
                        onClick={clearForm}
                        disabled={saving}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        <FaUndo />
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div
                className={`rounded-2xl shadow-xl border mb-6 ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">RISK/ISSUES ENTRY</h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-6">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Risk Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          value={formData.riskTitle}
                          onChange={(e) => handleInputChange("riskTitle", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter Title..."
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Risk/Issues
                        </label>
                        <textarea
                          rows="4"
                          value={formData.riskIssue}
                          onChange={(e) => handleInputChange("riskIssue", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Describe the risk or issue..."
                        />
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Responsibility <span className="text-red-500">*</span>
                        </label>
                        <input
                          value={formData.responsibility}
                          onChange={(e) => handleInputChange("responsibility", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Assign responsibility..."
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Impact/Mitigation/Resolution
                        </label>
                        <textarea
                          rows="4"
                          value={formData.resolution}
                          onChange={(e) => handleInputChange("resolution", e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Describe impact, mitigation and resolution..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div
                className={`rounded-2xl shadow-xl border overflow-hidden ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 flex items-center justify-between">
                  <h2 className="text-lg font-bold">RISK/ISSUES RECORDS</h2>
                  <div className="text-sm">
                    Total: <b>{totalCount}</b> | Selected: <b>{selectedCount}</b>
                  </div>
                </div>

                {loading ? (
                  <div className="p-10 text-center">
                    <FaSpinner className="animate-spin inline mr-2" />
                    Loading...
                  </div>
                ) : rows.length === 0 ? (
                  <div className="bg-blue-100 border border-blue-300 p-8">
                    <div className="text-center">
                      <FaExclamationTriangle className="mx-auto text-4xl text-blue-600 mb-4" />
                      <h3 className="text-xl font-bold text-blue-800 mb-2">No Records Found!</h3>
                      <p className="text-blue-700">No risk or issue records have been created yet.</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-blue-600 text-white">
                        <tr>
                          <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                            <input
                              type="checkbox"
                              checked={selectedIds.size === rows.length && rows.length > 0}
                              onChange={toggleSelectAll}
                              className="w-4 h-4"
                            />
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                            S.No
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                            Risk Title
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                            Risk/Issues
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500">
                            Responsibility
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                            Impact/Mitigation/Resolution
                          </th>
                        </tr>
                      </thead>

                      <tbody
                        className={`${
                          isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"
                        } divide-y`}
                      >
                        {rows.map((r, idx) => {
                          const id = Number(r.fld_Riskid);
                          const checked = selectedIds.has(id);

                          return (
                            <tr
                              key={id || idx}
                              className={`${
                                isDark ? "hover:bg-gray-800" : "hover:bg-blue-50"
                              } transition-colors`}
                            >
                              <td className="px-3 py-3 text-center border-r">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSelect(id)}
                                  className="w-4 h-4"
                                />
                              </td>

                              <td className="px-3 py-3 text-center border-r">{idx + 1}</td>

                              <td className="px-3 py-3 border-r">
                                <button
                                  onClick={() => handleView(id)}
                                  className="text-blue-600 hover:underline font-semibold text-left"
                                  title="Click to edit"
                                >
                                  {r.fld_Risktitle}
                                </button>
                              </td>

                              <td className="px-3 py-3 border-r">{r.fld_Riskissue}</td>
                              <td className="px-3 py-3 border-r">{r.fld_Responsibility}</td>
                              <td className="px-3 py-3">{r.fld_Resolution}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div
                className={`mt-6 p-6 rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={toggleSelectAll}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200"
                    >
                      {selectedIds.size === rows.length && rows.length > 0
                        ? "Clear All"
                        : "Select All"}
                    </button>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => navigate(-1)}
                      className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg"
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default RiskIssuesComponent;